import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './useAuth'
import { updateProfile, changePassword } from '../api/auth'
import type { ProfileUpdate } from '../types'
import { Layout } from '../components/Layout'
import { Input } from '../components/Input'
import { Button } from '../components/Button'
import styles from './ProfilePage.module.css'

const REGIONES = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana de Santiago', "O'Higgins", 'Maule',
  'Ñuble', 'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos',
  'Aysén', 'Magallanes',
]

const LICENSE_CLASSES = ['Sin licencia', 'A', 'B', 'C', 'D']

export function ProfilePage() {
  const { user, refreshProfile } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState<ProfileUpdate>({
    full_name: '',
    rut: '',
    license_class: '',
    license_number: '',
    phone: '',
    address: '',
    region: '',
  })
  const [saving, setSaving] = useState(false)

  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' })
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({})
  const [savingPw, setSavingPw] = useState(false)

  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name ?? '',
        rut: user.rut ?? '',
        license_class: user.license_class ?? '',
        license_number: user.license_number ?? '',
        phone: user.phone ?? '',
        address: user.address ?? '',
        region: user.region ?? '',
      })
    }
  }, [user])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const set = (field: keyof ProfileUpdate) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [field]: e.target.value }))

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await updateProfile(form)
      await refreshProfile()
      showToast('Datos guardados correctamente')
    } catch {
      showToast('No pudimos guardar los datos. Intenta nuevamente.')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs: Record<string, string> = {}
    if (!pwForm.current) errs.current = 'Ingresa tu contraseña actual.'
    if (!pwForm.next || pwForm.next.length < 8) errs.next = 'La nueva contraseña debe tener al menos 8 caracteres.'
    if (pwForm.next !== pwForm.confirm) errs.confirm = 'Las contraseñas no coinciden.'
    if (Object.keys(errs).length > 0) { setPwErrors(errs); return }
    setPwErrors({})
    setSavingPw(true)
    try {
      await changePassword({ current_password: pwForm.current, new_password: pwForm.next })
      setPwForm({ current: '', next: '', confirm: '' })
      setShowPasswordForm(false)
      showToast('Contraseña actualizada')
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      if (msg.includes('INVALID_CURRENT_PASSWORD') || msg.includes('422') || msg.includes('incorrecta')) {
        setPwErrors({ current: 'La contraseña actual es incorrecta.' })
      } else {
        setPwErrors({ form: 'No pudimos cambiar la contraseña. Intenta nuevamente.' })
      }
    } finally {
      setSavingPw(false)
    }
  }

  return (
    <Layout>
      <div className={styles.page}>

        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate('/')}>
            ← Volver
          </button>
          <div>
            <h1 className={styles.title}>Mi perfil</h1>
            <p className={styles.subtitle}>{user?.email}</p>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className={styles.toast} role="status" aria-live="polite">
            {toast}
          </div>
        )}

        {/* Datos del instalador */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Datos del instalador SEC</h2>
          <div className={styles.card}>
            <p className={styles.infoNote}>
              ℹ️ Estos datos se completan automáticamente en cada proyecto nuevo.
              Puedes editarlos por proyecto si es necesario.
            </p>
            <form onSubmit={handleSaveProfile} className={styles.form} noValidate>
              <Input
                label="Nombre completo"
                name="full_name"
                value={form.full_name ?? ''}
                onChange={set('full_name')}
                placeholder="Juan Pérez González"
              />
              <Input
                label="RUT"
                name="rut"
                value={form.rut ?? ''}
                onChange={set('rut')}
                placeholder="12.345.678-9"
              />
              <div className={styles.field}>
                <label className={styles.label}>Clase de licencia</label>
                <select
                  className={styles.select}
                  value={form.license_class ?? ''}
                  onChange={set('license_class')}
                  name="license_class"
                >
                  <option value="">Sin licencia</option>
                  {LICENSE_CLASSES.filter((c) => c !== 'Sin licencia').map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <Input
                label="Número de licencia SEC"
                name="license_number"
                value={form.license_number ?? ''}
                onChange={set('license_number')}
                placeholder="E-123456"
              />
              <Input
                label="Teléfono"
                name="phone"
                value={form.phone ?? ''}
                onChange={set('phone')}
                placeholder="+56 9 1234 5678"
              />
              <Input
                label="Dirección"
                name="address"
                value={form.address ?? ''}
                onChange={set('address')}
                placeholder="Av. Los Robles 123"
              />
              <div className={styles.field}>
                <label className={styles.label}>Región</label>
                <select
                  className={styles.select}
                  value={form.region ?? ''}
                  onChange={set('region')}
                  name="region"
                >
                  <option value="">Selecciona...</option>
                  {REGIONES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" loading={saving} fullWidth>
                Guardar datos
              </Button>
            </form>
          </div>
        </section>

        {/* Cuenta */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Cuenta</h2>
          <div className={styles.card}>
            <div className={styles.accountRow}>
              <span className={styles.accountLabel}>Correo electrónico</span>
              <span className={styles.accountValue}>{user?.email}</span>
            </div>
            <div className={styles.accountRow}>
              <span className={styles.accountLabel}>Plan</span>
              <span className={styles.planBadge}>Plan {user?.plan}</span>
            </div>
          </div>
        </section>

        {/* Contraseña */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Contraseña</h2>
          <div className={styles.card}>
            {user?.oauth_provider === 'google' ? (
              <div className={styles.googleNote}>
                <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" className={styles.googleIcon}>
                  <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.2l6.8-6.8C35.8 2.5 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.9 6.1C12.4 13.2 17.7 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.7 6c4.5-4.1 7-10.2 7-17.2z"/>
                  <path fill="#FBBC05" d="M10.5 28.6A14.8 14.8 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/>
                  <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.7-6c-2 1.4-4.6 2.2-7.5 2.2-6.3 0-11.6-3.7-13.5-9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/>
                </svg>
                <p className={styles.googleText}>
                  Tu contraseña está gestionada por Google. Para cambiarla, ve a tu cuenta de Google.
                </p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.togglePw}
                  onClick={() => setShowPasswordForm((s) => !s)}
                  aria-expanded={showPasswordForm}
                >
                  {showPasswordForm ? '▲' : '▼'} Cambiar contraseña
                </button>
                {showPasswordForm && (
                  <form onSubmit={handleChangePassword} className={styles.pwForm} noValidate>
                    <Input
                      label="Contraseña actual"
                      type="password"
                      name="current_password"
                      value={pwForm.current}
                      onChange={(e) => { setPwForm((p) => ({ ...p, current: e.target.value })); setPwErrors((p) => ({ ...p, current: '' })) }}
                      error={pwErrors.current}
                    />
                    <Input
                      label="Nueva contraseña"
                      type="password"
                      name="new_password"
                      value={pwForm.next}
                      onChange={(e) => { setPwForm((p) => ({ ...p, next: e.target.value })); setPwErrors((p) => ({ ...p, next: '' })) }}
                      error={pwErrors.next}
                    />
                    <Input
                      label="Confirmar nueva contraseña"
                      type="password"
                      name="confirm_password"
                      value={pwForm.confirm}
                      onChange={(e) => { setPwForm((p) => ({ ...p, confirm: e.target.value })); setPwErrors((p) => ({ ...p, confirm: '' })) }}
                      error={pwErrors.confirm}
                    />
                    {pwErrors.form && (
                      <p className={styles.errorMsg} role="alert">{pwErrors.form}</p>
                    )}
                    <Button type="submit" loading={savingPw} fullWidth>
                      Cambiar contraseña
                    </Button>
                  </form>
                )}
              </>
            )}
          </div>
        </section>

      </div>
    </Layout>
  )
}
